import { Badge } from "@/components/ui/badge";
import type { Member } from "@/types/Member";

interface OrgMembersListProps {
  members: Member[];
  ownerId?: string;
}

/**
 * Presentational list of organisation members.
 * Data is passed in (no fetch) so it can be consumed by both the owner screen
 * and admin/PM surfaces that already have the member list loaded.
 *
 * The owner row receives an "Owner" badge when `member.id === ownerId`.
 */
export default function OrgMembersList({ members, ownerId }: OrgMembersListProps) {
  if (members.length === 0) {
    return (
      <div data-testid="org-members-empty" className="py-8 text-center text-sm text-muted-foreground">
        No members in this organisation.
      </div>
    );
  }

  return (
    <ul className="divide-y divide-border" data-testid="org-members-list">
      {members.map((member) => {
        const isOwner = ownerId ? member.id === ownerId : false;
        return (
          <li
            key={member.id}
            data-testid={`org-member-row-${member.id}`}
            className="flex items-center gap-3 py-2 text-sm"
          >
            {/* Name */}
            <span className="flex-1 truncate font-medium">
              {member.firstName} {member.lastName}
            </span>

            {/* Email */}
            <span className="text-muted-foreground text-xs truncate">{member.email}</span>

            {/* Owner badge */}
            {isOwner && (
              <Badge variant="secondary" data-testid={`org-member-owner-badge-${member.id}`}>
                Owner
              </Badge>
            )}
          </li>
        );
      })}
    </ul>
  );
}
